/**
 * Turning an uploaded file into text the KB can chunk.
 *
 * Everything happens in the browser: the customer-facing pipeline only ever
 * receives TEXT, and the person uploading sees exactly what was extracted
 * before anything is stored — a PDF that extracts as rubbish is visible as
 * rubbish in the preview instead of embedding cleanly and answering wrongly.
 *
 * The parsers (mammoth for .docx, pdfjs for .pdf) are lazy imports: only the
 * person uploading pays for them, the inbox never does.
 */

export type FileKind = 'text' | 'docx' | 'pdf' | 'unsupported';

/** Routing by extension — MIME types from browsers are unreliable for these. */
export function kindOfFile(name: string): FileKind {
  const ext = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? '';
  if (['txt', 'md', 'markdown'].includes(ext)) return 'text';
  if (ext === 'docx') return 'docx';
  if (ext === 'pdf') return 'pdf';
  return 'unsupported';
}

export interface Extracted {
  text: string;
  /** Anything the uploader should know before trusting the text. */
  warnings: string[];
}

export async function extractText(file: File): Promise<Extracted> {
  switch (kindOfFile(file.name)) {
    case 'text':
      return { text: await file.text(), warnings: [] };

    case 'docx': {
      const mammoth = await import('mammoth');
      const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
      return {
        text: result.value,
        warnings: result.messages.map((message) => message.message),
      };
    }

    case 'pdf': {
      const pdfjs = await import('pdfjs-dist');
      const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

      const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
      const pages: string[] = [];
      for (let i = 1; i <= pdf.numPages; i += 1) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        pages.push(
          content.items
            .map((item) => ('str' in item ? item.str : ''))
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim(),
        );
      }
      const text = pages.filter(Boolean).join('\n\n');
      return {
        text,
        warnings:
          text.trim().length === 0
            ? ['This PDF contains no extractable text — it is probably a scan. OCR it first.']
            : [],
      };
    }

    default:
      throw new Error(
        `"${file.name}" is not a supported format. Upload .md, .txt, .docx or .pdf.`,
      );
  }
}
